import {
  createValidationResult,
  DependencyFunction,
  IModelContext,
  IValidationResult,
  ModelDependency,
  ModelItem,
  ValidationFunction,
  ValueOptions
} from '@mng/model';
import { findAntragdataInContext, findPersonInContext } from '../logic/person-model.logic';
import { dateToIsoDateString, getAlterForGeburtstag, isValidISODate } from '@mng/mng-ts-helpers';
import {
  AngebotMetaData,
  Antragdata,
  FatcaGmsg,
  GesetzlicherVertreter,
  Person,
  PersonRollen,
  PidInfoBestaetigung
} from '../../antrag.model.types';
import { findGesetzlicherVertreterFuerPerson } from '../logic/gesetzlicher-vertreter.logic';
import { MINDEST_ALTER_FUER_VN } from './person.helper';
import { IAntragFragen } from '../logic/antrag-fragen';
import { antragContext } from '../antrag-context';

const MARKT_PARTNER_NUMMER_ABKOMMEN_SANTANDER = 40700;
const MARKT_PARTNER_NUMMER_ABKOMMEN_HYPO_NOE = 9835;

// --------------
// PERSON ROLLEN
export const isPraemienzahlerReasonRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const data: PersonRollen = modelContext.dataStack[modelContext.dataStack.length - 1];
  return data.praemienzahler;
};
// --------------
// Treugeber
export const isTreugeberDefined: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const treuhaender: number = modelContext.value;
  const currentPerson: Person = modelContext.dataStack[modelContext.dataStack.length - 2];

  let treugeber = null;
  if (treuhaender === 1 && currentPerson.personRollen.treugeber && currentPerson.personRollen.treugeber.length > 0) {
    return createValidationResult(
      'treuhaender_must_not_be_treugeber',
      currentPerson,
      'Ein Treuhänder kann' + ' nicht gleichzeitig Treugeber sein'
    );
  }
  if (treuhaender === 1) {
    const personen: Person[] = modelContext.dataStack[modelContext.dataStack.length - 3];
    if (!personen) {
      throw Error('kein personen array');
    }

    treugeber = personen.filter(
      (person) =>
        person.personRollen &&
        person.personRollen.treugeber &&
        person.personRollen.treugeber.length > 0 &&
        person.personRollen.treugeber.find((tg) => tg.treuhaenderPersonId === currentPerson.id)
    );
    if (!treugeber || (treugeber && treugeber.length === 0)) {
      return createValidationResult('treugeber_not_defined', currentPerson, 'Treugeber muss angegeben werden');
    }
    if (treugeber.id === currentPerson.id) {
      return createValidationResult(
        'treugeber_equal_treuheaender',
        currentPerson,
        'Treugeber muss eine andere Person sein als der Treuhänder'
      );
    }
  }

  return null;
};
export const personRollenValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const data: PersonRollen = modelContext.value;

  if (!data) {
    return null;
  }
  if (data.praemienzahler && !data.praemienzahlerReason) {
    return createValidationResult('personRollen', data, 'Begründung muss eingegeben werden');
  }

  // Alterscheck fuer VN bzw. gesetzlichen Vertreter
  if (data.vn || (data.gesetzlicherVertreter && data.gesetzlicherVertreter.length > 0)) {
    const person: Person = modelContext.dataStack[modelContext.dataStack.length - 2];
    const alter: number = getAlterForGeburtstag(person.stammdaten.gebdatum);

    if ((alter === null || alter < MINDEST_ALTER_FUER_VN) && !person.juristischePerson) {
      if (data.vn) {
        // Gesetzlichen Vertreter suchen
        const personen: Person[] = modelContext.dataStack[modelContext.dataStack.length - 3];
        const gesetzlicheVertreterPerson: Person = findGesetzlicherVertreterFuerPerson(person, personen);

        // Gesetzlicher Vertreter muss vorhanden sein
        if (!gesetzlicheVertreterPerson) {
          return createValidationResult(
            'personRollen',
            data,
            'VN benötigt einen gesetzlichen Vertreter (Mindestalter für VN ist 18 Jahre)'
          );
        }
      }

      if (data.gesetzlicherVertreter && data.gesetzlicherVertreter.length > 0) {
        return createValidationResult('personRollen', data, 'Das Mindestalter für gesetzliche Vertreter ist 18 Jahre');
      }
    }
  }

  return null;
};

// --------------
// FATCA GMSG
export const fatcaGmsgValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const data: FatcaGmsg = modelContext.value;
  if (data && data.nurOesterreichSteuerpflichtig === 1 && data.usaSteuerpflichtig === 2) {
    return createValidationResult('fatcaGmsg Kombination ungültig');
  }
  return null;
};

export const gesetzlicherVertreterOptions: ValueOptions = new ValueOptions([
  { name: 'Elternteil', value: 1 },
  { name: 'Großelternteil', value: 2 },
  { name: 'Sachwalter', value: 3 },
  { name: 'Sonstige Funktion', value: 4 }
]);

export const isRolleSonstigeRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  const data: GesetzlicherVertreter = modelContext.dataStack[modelContext.dataStack.length - 1];
  return data.rolle === 4;
};

export const arbeitsStatusPersonValueOptions: ValueOptions = new ValueOptions([
  { name: 'Aktiv', value: 1 },
  { name: 'Ruhestand', value: 2 }
]);

export function hasAngebotWithTarifIsNotOfTyp(angebote: AngebotMetaData[], tarifTyp: string): boolean {
  return !!angebote.find((angebot: AngebotMetaData) => angebot.tarifInformationMetaData.tarifTyp !== tarifTyp);
}

export const isPersonPidBestaetigungRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  let person: Person = null;
  let antrag: Antragdata = null;
  if (modelContext.dataStack && modelContext.dataStack.length > 1) {
    person = findPersonInContext(modelContext);
    antrag = findAntragdataInContext(modelContext);
  }

  if (!person || !antrag) {
    throw new Error('isPersonPidBestaetigungRequired -> antrag / person rollen aus context nicht ermittelbar');
  }
  if (!person.personRollen) {
    return false;
  }
  const fragen: IAntragFragen = antragContext.getFragen(antrag, person);

  return fragen.erhaltbasisInfoBlatt;
};

export const isHypoNoeMarktPartnerNummerRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  let antrag: Antragdata = null;
  if (modelContext.dataStack && modelContext.dataStack.length > 1) {
    antrag = findAntragdataInContext(modelContext);
  }
  return (
    antrag?.vermittlerInfo?.abkommen && MARKT_PARTNER_NUMMER_ABKOMMEN_HYPO_NOE === antrag?.vermittlerInfo?.abkommen
  );
};

export const isSantanderMarktPartnerNummerRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  let antrag: Antragdata = null;
  if (modelContext.dataStack && modelContext.dataStack.length > 1) {
    antrag = findAntragdataInContext(modelContext);
  }
  return (
    antrag?.vermittlerInfo?.abkommen && MARKT_PARTNER_NUMMER_ABKOMMEN_SANTANDER === antrag?.vermittlerInfo?.abkommen
  );
};

export const isMarktPartnerNummerGroupRequired: DependencyFunction = (
  dependency: ModelDependency,
  modelContext: IModelContext
): boolean => {
  let antrag: Antragdata = null;
  if (modelContext.dataStack && modelContext.dataStack.length > 1) {
    antrag = findAntragdataInContext(modelContext);
  }
  return [MARKT_PARTNER_NUMMER_ABKOMMEN_SANTANDER, MARKT_PARTNER_NUMMER_ABKOMMEN_HYPO_NOE].includes(
    antrag?.vermittlerInfo?.abkommen
  );
};

export const personPidBestaetigungValidation: ValidationFunction = (
  modelItem: ModelItem,
  modelContext: IModelContext
): IValidationResult => {
  const pidInfoBestaetigung: PidInfoBestaetigung = modelContext.value;

  let validationResult: IValidationResult = null;

  // console.log('personPidBestaetigungValidation', pidInfoBestaetigung);

  if (pidInfoBestaetigung && isValidISODate(pidInfoBestaetigung.bestaetigungDatum)) {
    const currentDateString = dateToIsoDateString();
    if (pidInfoBestaetigung.bestaetigungDatum > currentDateString) {
      validationResult = createValidationResult(
        'pidBestaetigung',
        null,
        'Bestaetigung darf nicht in der Zukunft liegen'
      );
    } else {
      const minimalDate = new Date();
      minimalDate.setFullYear(minimalDate.getFullYear() - 1);
      minimalDate.setDate(minimalDate.getDate() + 1);
      const minimalDateString = dateToIsoDateString(minimalDate);
      if (pidInfoBestaetigung.bestaetigungDatum < minimalDateString) {
        validationResult = createValidationResult(
          'pidBestaetigung',
          null,
          'Bestaetigung darf nicht weiter als 1 Jahr zurückliegen'
        );
      }
    }
  } else {
    validationResult = createValidationResult('pidBestaetigung');
  }

  return validationResult;
};
